/* eslint-disable eqeqeq */
import React, { useEffect, useState, useMemo } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Row } from "../../../../../../../models/shipeezi.table";
import { TaskType } from "../../../../../../../models/task.type";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import useStyles from "./styles";
import TaskTypeModal from "./TaskTypeModal";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean
  }
}

const TaskTypePage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    isLoading,
    taskTypes,
    hasErrors,
    responseError,
    setResponseError,
    page,
    pageSize,
    total,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createTaskType,
    updateTaskType,
  } = useActions();

  const [selectedTaskType, setSelectedTaskType] = useState<TaskType>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const maxSequence = useMemo(() => {
    return Math.max(...taskTypes.map((taskType: TaskType) => Number(taskType.sequence ?? 0)));
  }, [taskTypes]);

  const editTaskType = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findTaskType = taskTypes.find((task) => task.id == index);

    if (findTaskType) {
      setSelectedTaskType(findTaskType);
      setOpenModal(true);
    }
  };

  const changeTaskTypeStatus = (index: number) => {
    const findTaskType = taskTypes.find((task) => task.id == index);

    if (findTaskType) {
      updateTaskType({
        id: Number(index),
        taskType: {
          ...findTaskType,
          status: findTaskType.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeAllStatusButton}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }

    return null;
  };

  const handleSaveTaskTypeSelected = (rowArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowArr, rows, taskTypes);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    { name: "sequence", title: translate("sequence") },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission?.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setSelectedTaskType(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        { name: translate("name"), column: "name" },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.task.type",
      });
      setHasErrors(undefined);
      setResponseError(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="taskTypeList">
      <ShipeeziNewGrid
        data={taskTypes}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission?.edit)
            ? (taskTypeId) => editTaskType(Number(taskTypeId))
            : undefined,
          handleChange: (taskTypeId) => changeTaskTypeStatus(Number(taskTypeId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveTaskTypeSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <TaskTypeModal
          open={openModal}
          closeModal={() => {
            setOpenModal(false);
            setResponseError(undefined);
          }}
          hasError={hasErrors}
          isLoading={isLoading}
          taskType={selectedTaskType}
          submit={(taskType: TaskType) => {
            if (taskType.id) updateTaskType({ id: taskType.id, taskType });
            else
              createTaskType({
                ...taskType,
                companyId: currentUser?.companyId,
              });
          }}
          maxSequence={maxSequence}
          responseError={responseError}
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateTaskType({ id: rowId, taskType: { status } as TaskType });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default TaskTypePage;
